// Detects that a newer build has been deployed while this tab was open.
//
// The server already sends index.html as no-cache and the JS bundle as immutable under a
// content-hashed name, so a FRESH page load always gets the latest code. What it cannot fix is a tab
// left open across a deploy: the app is a single page, so signing in or moving between screens never
// reloads the document, and yesterday's JavaScript keeps running against today's API.
//
// So rather than trying to defeat the cache, this compares what the server is serving right now with
// what this tab actually loaded. index.html is fetched with cache: 'no-store' and its main bundle
// filename read out; if it differs from the one running, a new build is live. No build step, no
// version file to remember to bump, and it compares the real artefacts rather than a stamp that
// could drift from them.

const BUNDLE_PATTERN = /\/static\/js\/(main\.[A-Za-z0-9]+\.js)/;

const CHECK_TIMEOUT_MS = 3000;

/** The hashed bundle this tab is running, or null under `npm start`, which has no hash. */
const runningBundle = () => {
  const el = document.querySelector('script[src*="/static/js/main."]');
  const src = el?.getAttribute('src') || '';
  return BUNDLE_PATTERN.exec(src)?.[1] || null;
};

/** False on the dev server, where there is no hashed bundle to compare and nothing to detect. */
export const versionCheckAvailable = () => runningBundle() !== null;

/**
 * True when the deployed bundle differs from the running one.
 *
 * <p>Never throws: a failed check means the network blipped or the server is mid-deploy, and nagging
 * the user about that would be worse than missing one update. Returns false in that case.
 */
export const isNewVersionDeployed = async () => {
  const running = runningBundle();
  if (!running) return false;
  // This runs on the sign-in path, so it is given a short deadline: a hanging request must never be
  // able to hold someone at the login screen. Timing out simply means "no new version".
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(`/index.html?v=${Date.now()}`, {
      cache: 'no-store',
      signal: abort.signal,
    });
    if (!res.ok) return false;
    const deployed = BUNDLE_PATTERN.exec(await res.text())?.[1];
    return Boolean(deployed) && deployed !== running;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

/** Full document reload, which re-fetches index.html and so picks up the new bundle. */
export const reloadToLatestVersion = () => window.location.reload();
